import { MAX_INVOICE_PDF_BYTES, moneyText, parseMoney } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Checkbox,
  FileInput,
  Loader,
  Modal,
  NativeSelect,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconFileInvoice,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconHistory,
  IconPlus,
  IconReceipt2,
  IconScale,
  IconTable,
  IconTrash,
} from '@tabler/icons-react';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDateTime } from '../../format.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import {
  formatDecimal,
  formatStatementDay,
  type StatementLabels,
  statementFileName,
  supplierStatementPdf,
  supplierStatementWorkbook,
} from '../../supplier-statement.js';
import { renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { XLSX_TYPE } from '../reports/xlsx.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './SupplierAccount.module.css';
import type {
  InvoiceStatus,
  OfficeInvoiceInputView,
  OfficeInvoiceTargetView,
  OfficeInvoiceView,
  OfficePaymentView,
  RfqOutcome,
  SupplierAccountData,
  SupplierAccountSource,
  SupplierHistoryData,
  SupplierStatementData,
} from './supplier-account.js';

const INVOICE_TONE: Record<InvoiceStatus, StatusTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};
const OUTCOME_TONE: Record<RfqOutcome, StatusTone> = {
  won: 'success',
  lost: 'neutral',
  cancelled: 'neutral',
  no_response: 'danger',
  pending: 'warning',
  open: 'info',
};

type K = (key: string, options?: Record<string, unknown>) => string;

function useK(): { k: K; language: string; t: ReturnType<typeof useTranslation>['t'] } {
  const { t, i18n } = useTranslation();
  const k: K = (key, options = {}) => t(`supplierAccount.${key}`, options);
  return { k, t, language: i18n.resolvedLanguage ?? 'en' };
}

function errorText(k: K, t: ReturnType<typeof useTranslation>['t'], code: string): string {
  return k(`errors.${code}`, {
    defaultValue: t(`purchasing.errors.${code}`, { defaultValue: t('purchasing.errors.GENERIC') }),
  });
}

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${today().slice(0, 4)}-01-01`;

// --- Sekmeler ---

function AccountTab({ data }: { data: SupplierAccountData }) {
  const { k, language } = useK();
  const money = (value: string, currency: string) =>
    `${formatDecimal(value, language)} ${currency}`;
  if (data.balances.length === 0) return <p className={classes.lead}>{k('balance.empty')}</p>;
  return (
    <>
      <p className={classes.lead}>{k('balance.lead')}</p>
      <section className={classes.cards} aria-label={k('balance.title')}>
        {data.balances.map((b) => {
          const advance = b.balance.startsWith('-');
          return (
            <article key={b.currency} className={classes.card}>
              <div className={classes.cardHead}>
                <span>{advance ? k('balance.advance') : k('balance.balance')}</span>
                <span>{b.currency}</span>
              </div>
              <p className={classes.amount}>
                {money(advance ? b.balance.slice(1) : b.balance, b.currency)}
              </p>
              <dl className={classes.facts}>
                <div>
                  <dt>{k('balance.invoiced')}</dt>
                  <dd>{formatDecimal(b.invoiced, language)}</dd>
                </div>
                <div>
                  <dt>{k('balance.paid')}</dt>
                  <dd>{formatDecimal(b.paid, language)}</dd>
                </div>
                <div>
                  <dt>{k('balance.pendingInvoices')}</dt>
                  <dd>{formatDecimal(b.pendingInvoices, language)}</dd>
                </div>
                <div>
                  <dt>{k('balance.pendingPayments')}</dt>
                  <dd>{formatDecimal(b.pendingPayments, language)}</dd>
                </div>
              </dl>
            </article>
          );
        })}
      </section>
      {data.aging.length > 0 ? (
        <section className={classes.section} aria-label={k('aging.title')}>
          <h3>{k('aging.title')}</h3>
          <section
            className={classes.tableWrap}
            aria-label={k('aging.title')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{k('statement.currency')}</th>
                  {(['notDue', 'd1_30', 'd31_60', 'd61_90', 'd90plus'] as const).map((key) => (
                    <th key={key} scope="col" data-numeric>
                      {k(`aging.${key}`)}
                    </th>
                  ))}
                  <th scope="col" data-numeric>
                    {k('aging.overdue')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.aging.map((a) => (
                  <tr key={a.currency}>
                    <td>{a.currency}</td>
                    {(['notDue', 'd1_30', 'd31_60', 'd61_90', 'd90plus'] as const).map((key) => (
                      <td key={key} data-numeric>
                        {formatDecimal(a.buckets[key], language)}
                      </td>
                    ))}
                    <td data-numeric>
                      <strong>{formatDecimal(a.overdue, language)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </section>
      ) : null}
    </>
  );
}

function MatchCell({ invoice }: { invoice: OfficeInvoiceView }) {
  const { k } = useK();
  const warnings = invoice.match?.warnings ?? [];
  if (warnings.length === 0) return <span className={classes.ok}>{k('invoices.matchOk')}</span>;
  return (
    <>
      {warnings.map((warning) => (
        <span key={warning} className={classes.warn}>
          {k(`warnings.${warning}`)}
        </span>
      ))}
    </>
  );
}

/**
 * Faturalar sekmesi: portal ve ofis faturaları (kaynak ve giren görünür; ADR 0119 Ek 2026-10-24).
 * "Enter invoice" düzenleme yetkisi ve dosya deposu varken görünür.
 */
export function InvoicesTab({
  data,
  source,
  onReview,
  onEnter,
}: {
  data: SupplierAccountData;
  source: SupplierAccountSource;
  onReview: (invoice: OfficeInvoiceView) => void;
  onEnter: () => void;
}) {
  const { k, language } = useK();
  const toolbar = (
    <div className={classes.toolbar}>
      <p className={classes.lead} style={{ flex: '1 1 320px', margin: 0 }}>
        {k('invoices.lead')}
      </p>
      {data.can.record && data.can.files ? (
        <Button onClick={onEnter}>{k('invoices.enter')}</Button>
      ) : null}
    </div>
  );
  if (data.invoices.length === 0) {
    return (
      <>
        {toolbar}
        <p className={classes.lead}>{k('invoices.empty')}</p>
      </>
    );
  }
  return (
    <>
      {toolbar}
      <section
        className={classes.tableWrap}
        aria-label={k('tabs.invoices')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={classes.table}>
          <thead>
            <tr>
              <th scope="col">{k('invoices.number')}</th>
              <th scope="col">{k('invoices.po')}</th>
              <th scope="col">{k('invoices.date')}</th>
              <th scope="col">{k('invoices.due')}</th>
              <th scope="col" data-numeric>
                {k('invoices.total')}
              </th>
              <th scope="col">{k('invoices.status')}</th>
              <th scope="col">{k('invoices.match')}</th>
              <th scope="col" data-numeric>
                {k('invoices.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td>
                  <strong>{invoice.invoiceNo}</strong>
                  {invoice.source === 'office' ? (
                    <span className={classes.sub}>
                      {[k('invoices.sourceOffice'), invoice.enteredBy?.userName]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : null}
                  {invoice.overBillingReason ? (
                    <span className={classes.warn}>
                      {k('invoices.overBilling', { reason: invoice.overBillingReason })}
                    </span>
                  ) : null}
                  {invoice.status === 'rejected' && invoice.reviewNote ? (
                    <span className={classes.sub}>
                      {k('invoices.rejectedNote', { note: invoice.reviewNote })}
                    </span>
                  ) : null}
                </td>
                <td>{invoice.poNo ?? '—'}</td>
                <td>{formatStatementDay(invoice.issueDate, language)}</td>
                <td>{formatStatementDay(invoice.dueDate, language)}</td>
                <td data-numeric>
                  {formatDecimal(invoice.total, language)} {invoice.currency}
                </td>
                <td>
                  <StatusBadge tone={INVOICE_TONE[invoice.status]}>
                    {k(`status.${invoice.status}`)}
                  </StatusBadge>
                  {invoice.review?.userName ? (
                    <span className={classes.sub}>{invoice.review.userName}</span>
                  ) : invoice.status === 'pending' && invoice.enteredBy?.userId === data.userId ? (
                    <span className={classes.sub}>{k('invoices.awaitingOther')}</span>
                  ) : null}
                </td>
                <td>
                  <MatchCell invoice={invoice} />
                </td>
                <td>
                  <div className={classes.actions}>
                    {data.can.files ? (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        component="a"
                        href={source.invoiceFileUrl(invoice.id)}
                        target="_blank"
                        rel="noopener"
                        aria-label={`${k('invoices.file')} ${invoice.invoiceNo}`}
                      >
                        {k('invoices.file')}
                      </Button>
                    ) : null}
                    {invoice.status === 'pending' && data.can.review ? (
                      <Button size="compact-xs" onClick={() => onReview(invoice)}>
                        {k('invoices.review')}
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

type PaymentAction = { payment: OfficePaymentView; action: 'approved' | 'rejected' | 'reverse' };

function PaymentsTab({
  data,
  source,
  onRecord,
  onAction,
}: {
  data: SupplierAccountData;
  source: SupplierAccountSource;
  onRecord: () => void;
  onAction: (action: PaymentAction) => void;
}) {
  const { k, language } = useK();
  return (
    <>
      <div className={classes.toolbar}>
        <p className={classes.lead} style={{ flex: '1 1 320px', margin: 0 }}>
          {k('payments.lead')}
        </p>
        {data.can.record ? (
          <Button onClick={onRecord} disabled={data.banks.length === 0}>
            {k('payments.record')}
          </Button>
        ) : null}
      </div>
      {data.can.record && data.banks.length === 0 ? (
        <p className={classes.warnBox} role="note">
          {k('payments.noBank')}
        </p>
      ) : null}
      {data.payments.length === 0 ? (
        <p className={classes.lead}>{k('payments.empty')}</p>
      ) : (
        <section
          className={classes.tableWrap}
          aria-label={k('tabs.payments')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={classes.table}>
            <thead>
              <tr>
                <th scope="col">{k('payments.date')}</th>
                <th scope="col">{k('payments.reference')}</th>
                <th scope="col" data-numeric>
                  {k('payments.amount')}
                </th>
                <th scope="col">{k('payments.account')}</th>
                <th scope="col">{k('payments.invoice')}</th>
                <th scope="col">{k('payments.status')}</th>
                <th scope="col" data-numeric>
                  {k('invoices.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((payment) => {
                const reversed = payment.reversedBy !== null;
                const status =
                  reversed && payment.status === 'approved' ? 'reversed' : payment.status;
                const own = payment.recordedBy.userId === data.userId;
                return (
                  <tr key={payment.id}>
                    <td>{formatStatementDay(payment.valueDate, language)}</td>
                    <td>
                      {payment.reference}
                      {payment.note ? <span className={classes.sub}>{payment.note}</span> : null}
                    </td>
                    <td data-numeric>
                      {payment.kind === 'reversal' ? '−' : ''}
                      {formatDecimal(payment.amount, language)} {payment.currency}
                    </td>
                    <td>
                      {payment.bank ? (
                        <>
                          {payment.bank.name}
                          <span className={classes.sub}>{payment.bank.masked}</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{payment.invoiceNo ?? '—'}</td>
                    <td>
                      <StatusBadge
                        tone={
                          status === 'approved'
                            ? 'success'
                            : status === 'pending'
                              ? 'warning'
                              : status === 'reversed'
                                ? 'neutral'
                                : 'danger'
                        }
                      >
                        {k(`paymentStatus.${status}`)}
                      </StatusBadge>
                      <span className={classes.sub}>
                        {k('payments.recordedBy')}: {payment.recordedBy.userName ?? '—'}
                      </span>
                      {payment.decision ? (
                        <span className={classes.sub}>
                          {k('payments.approvedBy')}: {payment.decision.userName ?? '—'}
                        </span>
                      ) : own && payment.status === 'pending' ? (
                        <span className={classes.sub}>{k('payments.awaitingOther')}</span>
                      ) : null}
                    </td>
                    <td>
                      <div className={classes.actions}>
                        {payment.receipt && data.can.files ? (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            component="a"
                            href={source.receiptUrl(payment.id)}
                            target="_blank"
                            rel="noopener"
                          >
                            {k('payments.receipt')}
                          </Button>
                        ) : null}
                        {payment.status === 'pending' && data.can.review && !own ? (
                          <Button
                            size="compact-xs"
                            onClick={() => onAction({ payment, action: 'approved' })}
                          >
                            {k('payments.approve')}
                          </Button>
                        ) : null}
                        {payment.status === 'pending' && data.can.review ? (
                          <Button
                            size="compact-xs"
                            variant="default"
                            onClick={() => onAction({ payment, action: 'rejected' })}
                          >
                            {k('payments.reject')}
                          </Button>
                        ) : null}
                        {payment.kind === 'payment' &&
                        payment.status === 'approved' &&
                        !reversed &&
                        data.can.record ? (
                          <Button
                            size="compact-xs"
                            variant="default"
                            onClick={() => onAction({ payment, action: 'reverse' })}
                          >
                            {k('payments.reverse')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

/** Ekstre belgesinin metinleri (`supplierAccount.statementDoc`): ofis ve portal aynı sözlükten. */
export function statementLabelsOf(t: (key: string) => string): StatementLabels {
  return statementLabels((key) => t(`supplierAccount.${key}`));
}

function statementLabels(k: K): StatementLabels {
  const l = (key: string) => k(`statementDoc.${key}`);
  return {
    title: l('title'),
    buyer: l('buyer'),
    supplier: l('supplier'),
    period: l('period'),
    currency: l('currency'),
    printed: l('printed'),
    opening: l('opening'),
    closing: l('closing'),
    totals: l('totals'),
    date: l('date'),
    type: l('type'),
    reference: l('reference'),
    due: l('due'),
    debit: l('debit'),
    credit: l('credit'),
    balance: l('balance'),
    kinds: {
      invoice: l('kinds.invoice'),
      payment: l('kinds.payment'),
      reversal: l('kinds.reversal'),
    },
    empty: l('empty'),
    footer: l('footer'),
  };
}

function StatementTab({
  supplierId,
  supplierName,
  data,
  source,
}: {
  supplierId: string;
  supplierName: string;
  data: SupplierAccountData;
  source: SupplierAccountSource;
}) {
  const { k, t, language } = useK();
  const currencies = useMemo(
    () => [...new Set(data.balances.map((b) => b.currency))].sort(),
    [data.balances],
  );
  const [currency, setCurrency] = useState(currencies[0] ?? '');
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [view, setView] = useState<SupplierStatementData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Uygulanan aralık: para birimi değişince hemen, tarih "Göster" ile
  const [applied, setApplied] = useState({ currency, from, to });
  const load = useCallback(() => {
    setApplied({ currency, from, to });
  }, [currency, from, to]);
  useEffect(() => {
    if (!applied.currency) return;
    setError(null);
    source.statement(supplierId, applied).then(setView, (failure) => setError(errorCode(failure)));
  }, [applied, source, supplierId]);

  if (!currency) return <p className={classes.lead}>{k('statement.empty')}</p>;
  const doc = async () => ({
    statement: view as SupplierStatementData,
    buyerName: await source.companyName(),
    supplierName,
    printedAt: formatDateTime(new Date().toISOString()),
    language,
    labels: statementLabels(k),
  });
  return (
    <>
      <form
        className={classes.toolbar}
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          load();
        }}
      >
        <NativeSelect
          label={k('statement.currency')}
          data={currencies}
          value={currency}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setCurrency(next);
            setApplied({ currency: next, from, to });
          }}
        />
        <TextInput
          type="date"
          label={k('statement.from')}
          value={from}
          onChange={(event) => setFrom(event.currentTarget.value)}
        />
        <TextInput
          type="date"
          label={k('statement.to')}
          value={to}
          onChange={(event) => setTo(event.currentTarget.value)}
        />
        <Button type="submit" variant="default" leftSection={<IconTable size={16} />}>
          {k('statement.show')}
        </Button>
        <span className={classes.spacer} />
        <Button
          variant="default"
          leftSection={<IconFileTypePdf size={16} />}
          disabled={!view}
          onClick={async () => {
            if (!view) return;
            const bytes = await renderPdf(supplierStatementPdf(await doc()));
            await source.download(
              statementFileName(supplierName, view, 'pdf'),
              bytes,
              'application/pdf',
            );
          }}
        >
          {k('statement.pdf')}
        </Button>
        <Button
          variant="default"
          leftSection={<IconFileSpreadsheet size={16} />}
          disabled={!view}
          onClick={async () => {
            if (!view) return;
            const bytes = await supplierStatementWorkbook(await doc());
            await source.download(statementFileName(supplierName, view, 'xlsx'), bytes, XLSX_TYPE);
          }}
        >
          {k('statement.excel')}
        </Button>
      </form>
      {error ? (
        <p className={classes.error} role="alert">
          {errorText(k, t, error)}
        </p>
      ) : null}
      {view ? <StatementTable view={view} /> : <Loader size="sm" />}
    </>
  );
}

/** Ekstrenin ekrandaki tablosu: açılış, hareketler yürüyen bakiyeyle, toplam ve kapanış. */
export function StatementTable({ view }: { view: SupplierStatementData }) {
  const { k, language } = useK();
  const l = (key: string) => k(`statementDoc.${key}`);
  const money = (value: string) => (value === '0.00' ? '' : formatDecimal(value, language));
  return (
    <section
      className={classes.tableWrap}
      aria-label={k('tabs.statement')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table} aria-label={l('title')}>
        <thead>
          <tr>
            <th scope="col">{l('date')}</th>
            <th scope="col">{l('type')}</th>
            <th scope="col">{l('reference')}</th>
            <th scope="col">{l('due')}</th>
            <th scope="col" data-numeric>
              {l('debit')}
            </th>
            <th scope="col" data-numeric>
              {l('credit')}
            </th>
            <th scope="col" data-numeric>
              {l('balance')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{formatStatementDay(view.from, language)}</td>
            <td colSpan={5}>
              <em>{l('opening')}</em>
            </td>
            <td data-numeric>
              <strong>{formatDecimal(view.opening, language)}</strong>
            </td>
          </tr>
          {view.entries.map((entry) => (
            <tr key={`${entry.kind}-${entry.reference}-${entry.date}-${entry.balance}`}>
              <td>{formatStatementDay(entry.date, language)}</td>
              <td>{l(`kinds.${entry.kind}`)}</td>
              <td>{entry.reference}</td>
              <td>{entry.dueDate ? formatStatementDay(entry.dueDate, language) : ''}</td>
              <td data-numeric>{money(entry.debit)}</td>
              <td data-numeric>{money(entry.credit)}</td>
              <td data-numeric>{formatDecimal(entry.balance, language)}</td>
            </tr>
          ))}
          <tr>
            <td>{formatStatementDay(view.to, language)}</td>
            <td colSpan={3}>
              <strong>{l('closing')}</strong>
            </td>
            <td data-numeric>
              <strong>{formatDecimal(view.totals.debit, language)}</strong>
            </td>
            <td data-numeric>
              <strong>{formatDecimal(view.totals.credit, language)}</strong>
            </td>
            <td data-numeric>
              <strong>
                {formatDecimal(view.closing, language)} {view.currency}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

/** Geçmiş (ADR 0119 §7): ofiste ve portalda aynı görünüm; rakip bilgisi yok. */
export function HistoryView({ data }: { data: SupplierHistoryData }) {
  const { k, language } = useK();
  const rate = data.responseRate;
  return (
    <>
      <section className={classes.cards} aria-label={k('history.rate')}>
        <article className={classes.card}>
          <div className={classes.cardHead}>
            <span>{k('history.rate')}</span>
          </div>
          <p className={classes.amount}>
            {rate.rate === null ? '—' : `${Math.round(rate.rate * 100)}%`}
          </p>
          <p className={classes.sub}>
            {rate.rate === null
              ? k('history.rateNone')
              : k('history.rateValue', { responded: rate.responded, invited: rate.invited })}
          </p>
        </article>
        <article className={classes.card}>
          <div className={classes.cardHead}>
            <span>{k('history.openMarket')}</span>
          </div>
          <p className={classes.amount}>{rate.openMarketQuotes}</p>
        </article>
      </section>
      <section className={classes.section} aria-label={k('history.requests')}>
        <h3>{k('history.requests')}</h3>
        {data.requests.length === 0 ? (
          <p className={classes.lead}>{k('history.empty')}</p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={k('history.requests')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{k('history.rfq')}</th>
                  <th scope="col">{k('history.vessel')}</th>
                  <th scope="col">{k('history.closing')}</th>
                  <th scope="col">{k('history.invited')}</th>
                  <th scope="col" data-numeric>
                    {k('history.revisions')}
                  </th>
                  <th scope="col">{k('history.outcome')}</th>
                </tr>
              </thead>
              <tbody>
                {data.requests.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.rfqNo}</strong>
                    </td>
                    <td>{row.vesselName}</td>
                    <td>
                      {row.closingAt
                        ? formatStatementDay(row.closingAt.slice(0, 10), language)
                        : '—'}
                    </td>
                    <td>{row.invited ? k('history.yes') : k('history.no')}</td>
                    <td data-numeric>{row.revisions}</td>
                    <td>
                      <StatusBadge tone={OUTCOME_TONE[row.outcome]}>
                        {k(`outcome.${row.outcome}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>
      <section className={classes.section} aria-label={k('history.orders')}>
        <h3>{k('history.orders')}</h3>
        {data.orders.length === 0 ? (
          <p className={classes.lead}>{k('history.empty')}</p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={k('history.orders')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{k('invoices.po')}</th>
                  <th scope="col">{k('invoices.date')}</th>
                  <th scope="col">{k('history.vessel')}</th>
                  <th scope="col" data-numeric>
                    {k('invoices.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <strong>{order.poNo}</strong>
                      {order.cancelled ? (
                        <span className={classes.sub}>{k('history.cancelled')}</span>
                      ) : null}
                    </td>
                    <td>{order.orderDate ? formatStatementDay(order.orderDate, language) : '—'}</td>
                    <td>{order.vesselName ?? '—'}</td>
                    <td data-numeric>
                      {formatDecimal(order.total, language)} {order.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>
      <section className={classes.section} aria-label={k('history.payments')}>
        <h3>{k('history.payments')}</h3>
        {data.payments.length === 0 ? (
          <p className={classes.lead}>{k('history.empty')}</p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={k('history.payments')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th scope="col">{k('payments.date')}</th>
                  <th scope="col">{k('payments.reference')}</th>
                  <th scope="col">{k('payments.invoice')}</th>
                  <th scope="col" data-numeric>
                    {k('payments.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatStatementDay(payment.valueDate, language)}</td>
                    <td>{payment.reference}</td>
                    <td>{payment.invoiceNo ?? '—'}</td>
                    <td data-numeric>
                      {payment.kind === 'reversal' ? '−' : ''}
                      {formatDecimal(payment.amount, language)} {payment.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>
    </>
  );
}

function HistoryTab({ supplierId, source }: { supplierId: string; source: SupplierAccountSource }) {
  const { k, t } = useK();
  const [data, setData] = useState<SupplierHistoryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    source.history(supplierId).then(setData, (failure) => setError(errorCode(failure)));
  }, [source, supplierId]);
  if (error) {
    return (
      <p className={classes.error} role="alert">
        {errorText(k, t, error)}
      </p>
    );
  }
  return data ? <HistoryView data={data} /> : <Loader size="sm" aria-label={k('loading')} />;
}

// --- Pencereler ---

function ReviewDialog({
  invoice,
  userId,
  onClose,
  onConfirm,
}: {
  invoice: OfficeInvoiceView;
  /** Oturumdaki kişi: ofis faturasını giren onaylayamaz (#1501). */
  userId: string;
  onClose: () => void;
  onConfirm: (input: {
    action: 'approved' | 'rejected';
    note?: string;
    acknowledgeWarnings?: boolean;
    password?: string;
  }) => Promise<string | null>;
}) {
  const { k, t, language } = useK();
  const reauth = useReauth('supplier_invoice_review');
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState<'approved' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const warnings = invoice.match?.warnings ?? [];
  const own = invoice.source === 'office' && invoice.enteredBy?.userId === userId;
  const money = (value: string | undefined) =>
    value === undefined ? '—' : `${formatDecimal(value, language)} ${invoice.currency}`;
  const submit = async (action: 'approved' | 'rejected') => {
    setBusy(action);
    const code = await onConfirm({
      action,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(action === 'approved' && warnings.length > 0 ? { acknowledgeWarnings: ack } : {}),
      ...reauth.body(),
    });
    setBusy(null);
    if (code) {
      reauth.noteError({ code });
      setError(code);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('review.title', { no: invoice.invoiceNo })}
      size="lg"
      closeButtonProps={{ 'aria-label': k('review.back') }}
    >
      <dl className={classes.matchGrid}>
        <div>
          <dt>{k('invoices.total')}</dt>
          <dd>{money(invoice.total)}</dd>
        </div>
        <div>
          <dt>{k('invoices.due')}</dt>
          <dd>{formatStatementDay(invoice.dueDate, language)}</dd>
        </div>
        <div>
          <dt>{k('review.poTotal')}</dt>
          <dd>{money(invoice.match?.poTotal)}</dd>
        </div>
        <div>
          <dt>{k('review.received')}</dt>
          <dd>{money(invoice.match?.received)}</dd>
        </div>
        <div>
          <dt>{k('review.invoiced')}</dt>
          <dd>{money(invoice.match?.invoiced)}</dd>
        </div>
        <div>
          <dt>{k('review.approved')}</dt>
          <dd>{money(invoice.match?.approved)}</dd>
        </div>
      </dl>
      {own ? (
        <p className={classes.warnBox} role="note">
          {k('review.ownEntry')}
        </p>
      ) : null}
      {warnings.length > 0 ? (
        <div className={classes.warnBox} role="note">
          <ul>
            {warnings.map((warning) => (
              <li key={warning}>{k(`warnings.${warning}`)}</li>
            ))}
          </ul>
          {invoice.overBillingReason ? (
            <p style={{ margin: '6px 0 0' }}>
              {k('invoices.overBilling', { reason: invoice.overBillingReason })}
            </p>
          ) : null}
        </div>
      ) : null}
      <Textarea
        label={k('review.rejectNote')}
        value={note}
        onChange={(event) => setNote(event.currentTarget.value)}
        maxLength={1000}
        autosize
        minRows={2}
        mb="sm"
      />
      {warnings.length > 0 ? (
        <Checkbox
          label={k('review.acknowledge')}
          checked={ack}
          onChange={(event) => setAck(event.currentTarget.checked)}
          mb="sm"
        />
      ) : null}
      <ReauthField reauth={reauth} label={t('purchasing.password.password')} />
      {error ? (
        <p className={classes.error} role="alert">
          {errorText(k, t, error)}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {k('review.back')}
        </Button>
        <Button
          color="red"
          variant="light"
          loading={busy === 'rejected'}
          disabled={note.trim().length < 5 || reauth.missing}
          onClick={() => void submit('rejected')}
        >
          {k('review.reject')}
        </Button>
        <Button
          loading={busy === 'approved'}
          disabled={own || (warnings.length > 0 && !ack) || reauth.missing}
          onClick={() => void submit('approved')}
        >
          {k('review.approve')}
        </Button>
      </div>
    </Modal>
  );
}

function PaymentDialog({
  data,
  onClose,
  onSave,
}: {
  data: SupplierAccountData;
  onClose: () => void;
  onSave: (input: {
    bankAccountId: string;
    currency: string;
    amount: string;
    valueDate: string;
    reference: string;
    note: string | null;
    invoiceId: string | null;
    receipt: File;
  }) => Promise<string | null>;
}) {
  const { k, t, language } = useK();
  const first = data.banks.find((bank) => bank.isDefault) ?? data.banks[0];
  const [bankId, setBankId] = useState(first?.bankAccountId ?? '');
  const bank = data.banks.find((b) => b.bankAccountId === bankId);
  const [amount, setAmount] = useState('');
  const [valueDate, setValueDate] = useState(today());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const outstanding = new Map(
    data.aging.flatMap((a) => a.items.map((item) => [item.id, item.outstanding] as const)),
  );
  const invoices = data.invoices.filter(
    (invoice) =>
      invoice.status === 'approved' &&
      invoice.currency === bank?.currency &&
      (outstanding.get(invoice.id) ?? '0.00') !== '0.00',
  );
  const valid =
    bank !== undefined &&
    /^\d{1,13}(\.\d{1,2})?$/.test(amount.trim()) &&
    Number(amount) > 0 &&
    reference.trim() !== '' &&
    receipt !== null;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('payDialog.title')}
      size="lg"
      closeButtonProps={{ 'aria-label': k('payDialog.cancel') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!valid || !bank || !receipt) return;
          setBusy(true);
          const code = await onSave({
            bankAccountId: bank.bankAccountId,
            currency: bank.currency,
            amount: amount.trim(),
            valueDate,
            reference: reference.trim(),
            note: note.trim() || null,
            invoiceId: invoiceId || null,
            receipt,
          });
          setBusy(false);
          if (code) setError(code);
        }}
      >
        <p className={classes.lead}>{k('payDialog.help')}</p>
        <NativeSelect
          label={k('payDialog.account')}
          data={data.banks.map((b) => ({
            value: b.bankAccountId,
            label: `${b.bankName} · ${b.masked} · ${b.currency}`,
          }))}
          value={bankId}
          onChange={(event) => {
            setBankId(event.currentTarget.value);
            setInvoiceId('');
          }}
          mb="sm"
        />
        <div className={classes.matchGrid}>
          <TextInput
            label={`${k('payDialog.amount')} (${bank?.currency ?? ''})`}
            inputMode="decimal"
            required
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
          <TextInput
            type="date"
            label={k('payDialog.valueDate')}
            required
            max={today()}
            value={valueDate}
            onChange={(event) => setValueDate(event.currentTarget.value)}
          />
        </div>
        <TextInput
          label={k('payDialog.reference')}
          required
          maxLength={80}
          value={reference}
          onChange={(event) => setReference(event.currentTarget.value)}
          mb="sm"
        />
        <NativeSelect
          label={k('payDialog.invoice')}
          data={[
            { value: '', label: k('payDialog.onAccount') },
            ...invoices.map((invoice) => ({
              value: invoice.id,
              label: `${invoice.invoiceNo} · ${formatDecimal(outstanding.get(invoice.id) ?? invoice.total, language)} ${invoice.currency}`,
            })),
          ]}
          value={invoiceId}
          onChange={(event) => setInvoiceId(event.currentTarget.value)}
          mb="sm"
        />
        <FileInput
          label={k('payDialog.receipt')}
          required
          accept="application/pdf,image/png,image/jpeg"
          value={receipt}
          onChange={setReceipt}
          mb="sm"
        />
        <Textarea
          label={k('payDialog.note')}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          maxLength={1000}
          autosize
          minRows={2}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {errorText(k, t, error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('payDialog.cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={!valid}>
            {k('payDialog.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type TaxLine = { label: string; rate: string; amount: string };
const plusDays = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00.000Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** Formun toplamı: net + vergi satırları (BigInt kuruş; geçersiz alan `null`). */
export function invoiceFormTotal(net: string, taxes: readonly TaxLine[]): string | null {
  const base = parseMoney(net.trim());
  if (base === null) return null;
  let sum = base;
  for (const line of taxes) {
    const amount = parseMoney(line.amount.trim());
    if (amount === null) return null;
    sum += amount;
  }
  return moneyText(sum);
}

/**
 * Ofisin tedarikçi adına fatura girişi (ADR 0119 Ek 2026-10-24): portalı kullanmayan tedarikçinin
 * faturası, ödüllü ya da ödülsüz onaylı PO'ya. Alanlar portal formuyla aynı; PDF zorunlu (8 MB).
 * Onayı başka kişi verir.
 */
function InvoiceEntryDialog({
  supplierId,
  data,
  source,
  onClose,
  onSave,
}: {
  supplierId: string;
  data: SupplierAccountData;
  source: SupplierAccountSource;
  onClose: () => void;
  onSave: (input: OfficeInvoiceInputView) => Promise<string | null>;
}) {
  const { k, t, language } = useK();
  const [targets, setTargets] = useState<OfficeInvoiceTargetView[] | null>(null);
  const [orderId, setOrderId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(plusDays(today(), 30));
  const [net, setNet] = useState('');
  const [taxes, setTaxes] = useState<TaxLine[]>([]);
  const [reason, setReason] = useState('');
  const [replaces, setReplaces] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    source
      .invoiceTargets(supplierId)
      .then((items) => {
        if (!live) return;
        setTargets(items);
        setOrderId(items[0]?.purchaseOrderId ?? '');
      })
      .catch((failure: unknown) => {
        if (!live) return;
        setTargets([]);
        setError(errorCode(failure));
      });
    return () => {
      live = false;
    };
  }, [source, supplierId]);
  const target = targets?.find((item) => item.purchaseOrderId === orderId);
  const currency = target?.currency ?? '';
  const total = invoiceFormTotal(net, taxes);
  const remaining = parseMoney(target?.remaining ?? '0.00') ?? 0n;
  const over = total !== null && (parseMoney(total) ?? 0n) > remaining;
  const rejected = data.invoices.filter((invoice) => invoice.status === 'rejected');
  const tooLarge = file !== null && file.size > MAX_INVOICE_PDF_BYTES;
  const valid =
    target !== undefined &&
    invoiceNo.trim() !== '' &&
    total !== null &&
    (parseMoney(net.trim()) ?? 0n) > 0n &&
    file !== null &&
    !tooLarge &&
    confirmed &&
    (!over || reason.trim().length >= 10);
  const setTax = (index: number, patch: Partial<TaxLine>) =>
    setTaxes((list) => list.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const money = (value: string) => `${formatDecimal(value, language)} ${currency}`;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('enterDialog.title')}
      size="lg"
      closeButtonProps={{ 'aria-label': k('enterDialog.cancel') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!valid || !file || !target || total === null) return;
          setBusy(true);
          setError(null);
          const code = await onSave({
            purchaseOrderId: target.purchaseOrderId,
            invoice: {
              invoiceNo: invoiceNo.trim(),
              issueDate,
              dueDate,
              currency,
              netAmount: net.trim(),
              taxLines: taxes.map((line) => ({
                label: line.label.trim(),
                rate: line.rate.trim() || '0',
                amount: line.amount.trim(),
              })),
              totalAmount: total,
              overBillingReason: over ? reason.trim() : null,
              replacesInvoiceId: replaces || null,
            },
            file,
          });
          setBusy(false);
          if (code) setError(code);
        }}
      >
        <p className={classes.lead}>{k('enterDialog.help')}</p>
        {targets === null ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 80 }}>
            <Loader size="sm" aria-label={k('enterDialog.loading')} />
          </div>
        ) : targets.length === 0 ? (
          <p className={classes.warnBox} role="note">
            {k('enterDialog.noTargets')}
          </p>
        ) : (
          <>
            <NativeSelect
              label={k('enterDialog.po')}
              data={targets.map((item) => ({
                value: item.purchaseOrderId,
                label: [
                  k('enterDialog.poOption', {
                    po: item.poNo,
                    remaining: `${formatDecimal(item.remaining, language)} ${item.currency}`,
                  }),
                  item.awardId ? null : k('enterDialog.noAward'),
                ]
                  .filter(Boolean)
                  .join(' · '),
              }))}
              value={orderId}
              onChange={(event) => setOrderId(event.currentTarget.value)}
              mb="sm"
            />
            <div className={classes.grid2}>
              <TextInput
                label={k('enterDialog.number')}
                required
                maxLength={40}
                value={invoiceNo}
                onChange={(event) => setInvoiceNo(event.currentTarget.value)}
              />
              <TextInput
                label={k('enterDialog.net', { currency })}
                required
                inputMode="decimal"
                value={net}
                onChange={(event) => setNet(event.currentTarget.value)}
              />
              <TextInput
                type="date"
                label={k('enterDialog.issueDate')}
                required
                max={today()}
                value={issueDate}
                onChange={(event) => setIssueDate(event.currentTarget.value)}
              />
              <TextInput
                type="date"
                label={k('enterDialog.dueDate')}
                required
                min={issueDate}
                value={dueDate}
                onChange={(event) => setDueDate(event.currentTarget.value)}
              />
            </div>
            {taxes.map((line, index) => (
              <div key={`tax-${index.toString()}`} className={classes.taxRow}>
                <TextInput
                  label={k('enterDialog.taxLabel')}
                  value={line.label}
                  maxLength={40}
                  onChange={(event) => setTax(index, { label: event.currentTarget.value })}
                />
                <TextInput
                  label={k('enterDialog.taxRate')}
                  inputMode="decimal"
                  value={line.rate}
                  onChange={(event) => setTax(index, { rate: event.currentTarget.value })}
                />
                <TextInput
                  label={k('enterDialog.taxAmount')}
                  inputMode="decimal"
                  value={line.amount}
                  onChange={(event) => setTax(index, { amount: event.currentTarget.value })}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  aria-label={k('enterDialog.removeTax')}
                  onClick={() => setTaxes((list) => list.filter((_, i) => i !== index))}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </div>
            ))}
            {taxes.length < 5 ? (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setTaxes((list) => [...list, { label: '', rate: '', amount: '' }])}
              >
                {k('enterDialog.addTax')}
              </Button>
            ) : null}
            <div className={classes.total}>
              <span>{k('enterDialog.total')}</span>
              <span>{total === null ? '—' : money(total)}</span>
            </div>
            {over && target ? (
              <Textarea
                label={k('enterDialog.overBilling')}
                description={k('enterDialog.overBillingHelp', {
                  remaining: money(target.remaining),
                })}
                required
                value={reason}
                maxLength={1000}
                autosize
                minRows={2}
                onChange={(event) => setReason(event.currentTarget.value)}
                mb="sm"
              />
            ) : null}
            {rejected.length > 0 ? (
              <NativeSelect
                label={k('enterDialog.replaces')}
                data={[
                  { value: '', label: k('enterDialog.replacesNone') },
                  ...rejected.map((invoice) => ({ value: invoice.id, label: invoice.invoiceNo })),
                ]}
                value={replaces}
                onChange={(event) => setReplaces(event.currentTarget.value)}
                mb="sm"
              />
            ) : null}
            <FileInput
              label={k('enterDialog.file')}
              required
              accept="application/pdf"
              value={file}
              onChange={setFile}
              error={tooLarge ? k('enterDialog.fileTooLarge') : undefined}
              mb="sm"
            />
            <Checkbox
              label={k('enterDialog.confirm')}
              checked={confirmed}
              onChange={(event) => setConfirmed(event.currentTarget.checked)}
            />
          </>
        )}
        {error ? (
          <p className={classes.error} role="alert">
            {k(`enterDialog.errors.${error}`, { defaultValue: errorText(k, t, error) })}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('enterDialog.cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={!valid}>
            {k('enterDialog.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// --- Ana pencere ---

export interface SupplierAccountDialogProps {
  supplierId: string;
  supplierName: string;
  source: SupplierAccountSource;
  onClose: () => void;
  onChanged?: (message: string) => void;
  /** Açılış sekmesi (zil bağlantısı `?account=<id>&tab=`); verilmezse "Account". */
  initialTab?: 'account' | 'invoices' | 'payments';
}

/**
 * Tedarikçi kartının "Account & history" penceresi (ADR 0119): bakiye ve vade, faturalar (üç
 * yönlü eşleştirmeyle inceleme), ödemeler (dört göz, ters kayıt), ekstre (PDF ve Excel) ve geçmiş.
 */
export function SupplierAccountDialog({
  supplierId,
  supplierName,
  source,
  onClose,
  onChanged,
  initialTab,
}: SupplierAccountDialogProps) {
  const { k, t, language } = useK();
  const narrow = useMediaQuery('(max-width: 767px)') ?? false;
  const [data, setData] = useState<SupplierAccountData | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(initialTab ?? 'account');
  const [reviewing, setReviewing] = useState<OfficeInvoiceView | null>(null);
  const [paying, setPaying] = useState(false);
  const [entering, setEntering] = useState(false);
  const [action, setAction] = useState<PaymentAction | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await source.get(supplierId));
    } catch (error) {
      setFailed(errorCode(error));
    }
  }, [source, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);

  const pendingInvoices = data?.invoices.filter((i) => i.status === 'pending').length ?? 0;
  const pendingPayments = data?.payments.filter((p) => p.status === 'pending').length ?? 0;
  const run = async (work: () => Promise<SupplierAccountData>, message: string) => {
    try {
      setData(await work());
      onChanged?.(message);
      return null;
    } catch (error) {
      const code = errorCode(error);
      if (code === 'CONTENT_CHANGED' || code === 'MATCH_CHANGED') void load();
      return code;
    }
  };
  const blocked = reviewing !== null || paying || entering || action !== null;

  return (
    <>
      <Modal
        opened={!blocked}
        onClose={onClose}
        title={k('title', { name: supplierName })}
        size="70rem"
        fullScreen={narrow}
        closeButtonProps={{ 'aria-label': k('close') }}
      >
        {failed ? (
          <p className={classes.error} role="alert">
            {errorText(k, t, failed)}
          </p>
        ) : !data ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 160 }}>
            <Loader aria-label={k('loading')} />
          </div>
        ) : (
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="account" leftSection={<IconScale size={16} />}>
                {k('tabs.account')}
              </Tabs.Tab>
              <Tabs.Tab
                value="invoices"
                leftSection={<IconFileInvoice size={16} />}
                rightSection={
                  pendingInvoices > 0 ? (
                    <StatusBadge tone="warning">{String(pendingInvoices)}</StatusBadge>
                  ) : null
                }
              >
                {k('tabs.invoices')}
              </Tabs.Tab>
              <Tabs.Tab
                value="payments"
                leftSection={<IconReceipt2 size={16} />}
                rightSection={
                  pendingPayments > 0 ? (
                    <StatusBadge tone="warning">{String(pendingPayments)}</StatusBadge>
                  ) : null
                }
              >
                {k('tabs.payments')}
              </Tabs.Tab>
              <Tabs.Tab value="statement" leftSection={<IconTable size={16} />}>
                {k('tabs.statement')}
              </Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
                {k('tabs.history')}
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="account">
              <AccountTab data={data} />
            </Tabs.Panel>
            <Tabs.Panel value="invoices">
              <InvoicesTab
                data={data}
                source={source}
                onReview={setReviewing}
                onEnter={() => setEntering(true)}
              />
            </Tabs.Panel>
            <Tabs.Panel value="payments">
              <PaymentsTab
                data={data}
                source={source}
                onRecord={() => setPaying(true)}
                onAction={setAction}
              />
            </Tabs.Panel>
            <Tabs.Panel value="statement">
              <StatementTab
                supplierId={supplierId}
                supplierName={supplierName}
                data={data}
                source={source}
              />
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <HistoryTab supplierId={supplierId} source={source} />
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
      {reviewing ? (
        <ReviewDialog
          invoice={reviewing}
          userId={data?.userId ?? ''}
          onClose={() => setReviewing(null)}
          onConfirm={async (input) => {
            const code = await run(
              () => source.review(reviewing.id, { ...input, contentHash: reviewing.contentHash }),
              k(`notices.${input.action}`),
            );
            if (!code) setReviewing(null);
            return code;
          }}
        />
      ) : null}
      {entering && data ? (
        <InvoiceEntryDialog
          supplierId={supplierId}
          data={data}
          source={source}
          onClose={() => setEntering(false)}
          onSave={async (input) => {
            const code = await run(
              () => source.enterInvoice(supplierId, input),
              k('notices.entered'),
            );
            if (!code) setEntering(false);
            return code;
          }}
        />
      ) : null}
      {paying && data ? (
        <PaymentDialog
          data={data}
          onClose={() => setPaying(false)}
          onSave={async (input) => {
            const code = await run(() => source.pay(supplierId, input), k('notices.paid'));
            if (!code) setPaying(false);
            return code;
          }}
        />
      ) : null}
      {action ? (
        <PasswordDialog
          opened
          title={k(
            `decision.${action.action === 'approved' ? 'approve' : action.action === 'rejected' ? 'reject' : 'reverse'}Title`,
          )}
          body={k(
            `decision.${action.action === 'approved' ? 'approve' : action.action === 'rejected' ? 'reject' : 'reverse'}Body`,
            {
              amount: `${formatDecimal(action.payment.amount, language)} ${action.payment.currency}`,
              account: action.payment.bank?.masked ?? '—',
            },
          )}
          reason={action.action === 'approved' ? 'none' : 'required'}
          reasonLabel={k('decision.reason')}
          password={action.action !== 'reverse'}
          reauthKind="supplier_payment_approval"
          errorScope="ledger"
          confirmLabel={k(
            `decision.${action.action === 'approved' ? 'approve' : action.action === 'rejected' ? 'reject' : 'reverse'}`,
          )}
          onClose={() => setAction(null)}
          onConfirm={async (input) => {
            const payment = action.payment;
            const code =
              action.action === 'reverse'
                ? await run(
                    () => source.reverse(payment.id, { reason: input.reason }),
                    k('notices.reversed'),
                  )
                : await run(
                    () =>
                      source.decide(payment.id, {
                        action: action.action as 'approved' | 'rejected',
                        contentHash: payment.contentHash,
                        ...(input.reason ? { note: input.reason } : {}),
                        ...(input.password ? { password: input.password } : {}),
                      }),
                    k(
                      action.action === 'approved'
                        ? 'notices.paymentApproved'
                        : 'notices.paymentRejected',
                    ),
                  );
            if (code) return code;
            setAction(null);
            return null;
          }}
        />
      ) : null}
    </>
  );
}
